import { useBrand } from '@/shared/brand/useBrand';
import { useTheme } from '@/shared/theme/useTheme';

interface Props {
  /**
   * - 'wordmark': full wordmark (logo + text).
   * - 'icon': square mark only (logo without text).
   * - 'responsive': icon on smaller screens, full wordmark from breakpoint up.
   */
  variant?: 'wordmark' | 'icon' | 'responsive';
  /** Breakpoint at which the wordmark appears in 'responsive' mode. Defaults to 'sm'. */
  wordmarkBreakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  /** Use the reverse wordmark when the surrounding surface is always dark. */
  tone?: 'auto' | 'light' | 'dark';
  className?: string;
}

/**
 * The active brand's mark. Never hardcode a logo path in a screen — the same
 * component renders Numtable or Procalendar depending on the hostname.
 */
export function BrandLogo({
  variant = 'wordmark',
  wordmarkBreakpoint = 'sm',
  tone = 'auto',
  className,
}: Props) {
  const brand = useBrand();
  const { resolved } = useTheme();

  const isDark = tone === 'dark' || (tone === 'auto' && resolved === 'dark');
  const wordmarkSrc = isDark ? brand.logoReverse : brand.logo;
  const iconSrc = isDark ? brand.iconReverse : brand.icon;

  if (variant === 'icon') {
    return (
      <img
        src={iconSrc}
        alt={brand.name}
        className={className ?? 'h-7 w-auto'}
        width={28}
        height={28}
        decoding="async"
      />
    );
  }

  if (variant === 'responsive') {
    const iconVisibility =
      wordmarkBreakpoint === 'sm'
        ? 'sm:hidden'
        : wordmarkBreakpoint === 'lg'
          ? 'lg:hidden'
          : wordmarkBreakpoint === 'xl'
            ? 'xl:hidden'
            : 'md:hidden';

    const wordmarkVisibility =
      wordmarkBreakpoint === 'sm'
        ? 'hidden sm:block'
        : wordmarkBreakpoint === 'lg'
          ? 'hidden lg:block'
          : wordmarkBreakpoint === 'xl'
            ? 'hidden xl:block'
            : 'hidden md:block';

    return (
      <>
        <img
          src={iconSrc}
          alt={brand.name}
          className={`${className ?? 'h-7 w-auto'} ${iconVisibility}`}
          width={28}
          height={28}
          decoding="async"
        />
        <img
          src={wordmarkSrc}
          alt={brand.name}
          className={`${className ?? 'h-7 w-auto'} ${wordmarkVisibility}`}
          width={120}
          height={28}
          decoding="async"
        />
      </>
    );
  }

  return (
    <img
      src={wordmarkSrc}
      alt={brand.name}
      className={className ?? 'h-7 w-auto'}
      width={120}
      height={28}
      decoding="async"
    />
  );
}
